/**
 * Credit-cost hint above the Create button (#188) — a one-line reminder that creating a
 * game spends a credit, shown at the moment of commitment. Rendered by both create lobbies
 * (`DynamicLobby`, and `PackLobby` for the paid auto-host path) so the copy has one source
 * of truth.
 *
 * Visible only when a balance is loaded and the host has at least one credit
 * (`availableCredits >= 1`); at `0` the Create button + its container are already withdrawn
 * (#171/PR #179), so the hint never appears there, and a free (human-hosted pack) game
 * passes `undefined` → nothing renders. The noun is smart-pluralized ("credit" at exactly
 * `1`, "credits" otherwise), so a host about to spend their last credit reads
 * "…your 1 available credit." Presentational only — no control (FR-9.5 preserved).
 */

interface CreateCostHintProps {
  /** The host's current credit balance (`balance.total` from #171). `undefined` when unknown. */
  availableCredits?: number;
  /** BEM class for the owning lobby (`dynamic-lobby__cost` / `pack-lobby__cost`). */
  className: string;
}

export function CreateCostHint({
  availableCredits,
  className,
}: CreateCostHintProps): React.JSX.Element | null {
  if (availableCredits == null || availableCredits < 1) return null;
  const noun = availableCredits === 1 ? 'credit' : 'credits';
  return (
    <p className={`${className} muted`} data-testid="create-cost-hint">
      Creating a game will consume 1 of your {availableCredits} available {noun}.
    </p>
  );
}
